/**
 * Shared Swym wishlist helpers for swym-wishlist.js, swym-wishlist-btn.js, swym-cart-saved.js.
 * Must load before those assets (defer order in Liquid).
 */
(function (global) {
  'use strict';

  if (global.SwymWishlistCore) return;

  var WISHLIST_LID_STORAGE_KEY = 'swym_wishlist_lid';

  var EU_COUNTRIES = [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
    'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
    'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'GB', 'CH', 'NO',
  ];

  function coerceAvailability(value) {
    if (value === true) return true;
    if (value === false) return false;
    if (value == null || value === '') return null;
    var n = Number(value);
    if (!isNaN(n)) return n > 0;
    return null;
  }

  function getMarketContext() {
    var region = null;
    var warehouse = null;

    try {
      var technicalEl = global.document && global.document.querySelector('[data-technical]');
      if (technicalEl) {
        var data = JSON.parse(technicalEl.textContent);
        if (data.localization) {
          if (data.localization.marketRegion) {
            region = data.localization.marketRegion;
          }
          if (data.localization.marketWarehouse) {
            warehouse = data.localization.marketWarehouse;
          }
          if (!region && data.localization.countryCode) {
            var country = data.localization.countryCode;
            if (country === 'US') region = 'US';
            else if (EU_COUNTRIES.indexOf(country) !== -1) region = 'EU';
            else region = 'International';
          }
        }
      }
    } catch (e) {
      /* ignore */
    }

    if (global.theme) {
      if (global.theme.region) region = global.theme.region;
      if (global.theme.warehouse) warehouse = global.theme.warehouse;
    }

    if (!region && global.theme && global.theme.country) {
      if (global.theme.country === 'US') region = 'US';
      else region = 'International';
    }

    if (!region) region = 'International';

    return { region: region, warehouse: warehouse };
  }

  function getRegion() {
    return getMarketContext().region;
  }

  function regionValueFromRow(row, region) {
    if (!row) return null;
    var rt = row[region];
    if (rt == null || rt === '') {
      var rl = (region || '').toLowerCase();
      if (rl === 'us') rt = row.US != null ? row.US : row.us;
      else if (rl === 'eu') rt = row.EU != null ? row.EU : row.eu;
      else rt = row.International != null ? row.International : row.international;
    }
    return coerceAvailability(rt);
  }

  function regionTotalFromInventory(il, region) {
    if (!il || !il.total) return null;
    return regionValueFromRow(il.total, region);
  }

  /**
   * Region-aware availability (mirrors theme.js ProductVariants + product-item.liquid).
   */
  function resolveRegionalAvailability(product, variant) {
    if (!product) {
      return { variantAvailable: false, totalAvailable: false };
    }

    if (typeof product.market_available === 'boolean') {
      var totalAvailable = product.market_available;
      var variantAvailable =
        variant && typeof variant.market_available === 'boolean'
          ? variant.market_available
          : totalAvailable;
      return { variantAvailable: variantAvailable, totalAvailable: totalAvailable };
    }

    var variantAvailable = variant ? variant.available : product.available;
    var totalAvailable = product.available;

    if (
      !global.theme ||
      !global.theme.availableByLocation ||
      !product.inventory_levels
    ) {
      return { variantAvailable: variantAvailable, totalAvailable: totalAvailable };
    }

    var region = getRegion();
    var il = product.inventory_levels;
    var totalCoerced = regionTotalFromInventory(il, region);
    if (totalCoerced !== null) totalAvailable = totalCoerced;

    if (variant && il.variants) {
      var byVariant =
        il.variants[variant.id] || il.variants[String(variant.id)];
      if (byVariant) {
        var variantCoerced = regionValueFromRow(byVariant, region);
        if (variantCoerced !== null) variantAvailable = variantCoerced;
      }
    }

    return { variantAvailable: variantAvailable, totalAvailable: totalAvailable };
  }

  /**
   * Zone-specific pre-sale tags only (mirrors product-pre-order.liquid / product-item.liquid).
   */
  function isPreorderForRegion(product) {
    if (!product) return false;

    var tags = product.tags || [];
    var tagsLower = tags.map(function (t) {
      return t.toLowerCase();
    });

    var stellaeDate = null;
    var nipponDate = null;

    tagsLower.forEach(function (tag) {
      if (tag.indexOf('stellae_pre-sale:') !== -1) stellaeDate = tag;
      else if (tag.indexOf('nippon_pre-sale:') !== -1) nipponDate = tag;
    });

    var ctx = getMarketContext();
    var dateTag = null;

    if (ctx.warehouse === 'Stellae' || ctx.region === 'US') {
      dateTag = stellaeDate;
    } else if (
      ctx.warehouse === 'Nippon' ||
      ctx.region === 'EU' ||
      ctx.region === 'International'
    ) {
      dateTag = nipponDate;
    }

    return !!dateTag;
  }

  /**
   * Pre-order line item properties for a product, as resolved server-side by the
   * `pre-order-resolve` snippet and serialised into the wishlist-data template.
   * Wishlist / saved-items add-to-cart must carry these or the order-tagging Flow
   * sees no pre-order property and tags the order `is_preorder=no` (ROW-974).
   */
  function preOrderProperties(product) {
    var props = product && product.pre_order_properties;
    if (!props || typeof props !== 'object') return null;
    return Object.keys(props).length ? props : null;
  }

  /** Serialise for stashing on a CTA button so add-to-cart needs no refetch. */
  function preOrderPropertiesAttr(product) {
    var props = preOrderProperties(product);
    return props ? JSON.stringify(props) : '';
  }

  /** Read back what preOrderPropertiesAttr stashed. Never throws. */
  function readPreOrderProperties(el) {
    if (!el) return null;
    var raw = el.getAttribute('data-preorder-props');
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      return parsed && Object.keys(parsed).length ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Selling plan id for a variant, when pre-orders are charged on fulfillment.
   * Mirrors the PDP form, which posts `selling_plan` instead of the pre-order
   * properties in that mode — without it a wishlist pre-order is billed as a
   * plain purchase (ROW-974).
   */
  function sellingPlanAttr(variant) {
    var id = variant && variant.selling_plan_id;
    return id ? String(id) : '';
  }

  /** Read back what sellingPlanAttr stashed. */
  function readSellingPlan(el) {
    if (!el) return null;
    var raw = el.getAttribute('data-selling-plan');
    return raw ? raw : null;
  }

  /** Button state for Saved page + mini-cart Saved (mirrors product-item.liquid / swym-wishlist). */
  function getButtonState(product, variant) {
    if (!product) {
      return { text: 'Sold Out', enabled: false, type: 'sold_out' };
    }

    var tags = product.tags || [];
    var tagsLower = tags.map(function (t) {
      return t.toLowerCase();
    });
    var avail = resolveRegionalAvailability(product, variant);
    var variantAvailable = avail.variantAvailable;
    var totalAvailable = avail.totalAvailable;
    var inStock = totalAvailable && (!variant || variantAvailable);

    if (tagsLower.indexOf('coming_soon') !== -1) {
      return { text: 'Coming Soon', enabled: false, type: 'coming_soon' };
    }

    var isInStoreOnly = tagsLower.indexOf('availability_instore only') !== -1;
    var isPriceUponRequest = tagsLower.indexOf('price_upon_request') !== -1;
    if ((isInStoreOnly || isPriceUponRequest) && !totalAvailable) {
      return { text: 'In-Store Only', enabled: false, type: 'instore' };
    }

    if (inStock) {
      if (isPreorderForRegion(product)) {
        return { text: 'Pre-Order', enabled: true, type: 'preorder' };
      }
      return { text: 'Add to Bag', enabled: true, type: 'add' };
    }

    // Out of stock for browsing market — no add / pre-order CTA
    if (tagsLower.indexOf('margaux_bags_c_segment') !== -1) {
      return { text: 'Sold Out', enabled: false, type: 'sold_out' };
    }

    if (
      product.hide_sold_out_on_collection ||
      tagsLower.indexOf('disallow_notify_me') !== -1 ||
      (global.theme && global.theme.allowBackInStock === false)
    ) {
      return { text: 'Sold Out', enabled: false, type: 'sold_out' };
    }

    var notifyLabel =
      global.swymWishlistStrings && global.swymWishlistStrings.notifyMe
        ? global.swymWishlistStrings.notifyMe
        : 'Notify Me';
    return { text: notifyLabel, enabled: true, type: 'notify' };
  }

  function openCartDrawer() {
    var cartEl = global.document && global.document.querySelector('#sidebar-cart');
    if (cartEl && typeof cartEl.openCartDrawer === 'function') {
      return cartEl.openCartDrawer() === true;
    }
    return false;
  }

  function openUniversalDrawer() {
    if (global.theme && global.theme.universalDrawerControl) {
      global.theme.universalDrawerControl.open();
      return;
    }
    var opener =
      global.document.getElementById('SwymWishlist__OpenUniversalDrawer') ||
      global.document.getElementById('CartDrawer__OpenUniversalDrawer') ||
      global.document.querySelector(
        '[data-action="open-drawer"][data-drawer-id="universal-drawer"]',
      );
    if (opener) opener.click();
  }

  function openNotifyMeForVariant(variantId, product) {
    var bisFeedCode =
      global.theme && global.theme.bisFeedCode ? global.theme.bisFeedCode : null;
    var tags = product && product.tags ? product.tags : [];
    var tagsArr = Array.isArray(tags)
      ? tags
      : String(tags).split(',').map(function (t) { return t.trim(); });
    var isRegisterInterest = tagsArr.indexOf('alternative_notify_me') !== -1;
    var variantRelationshipId = isRegisterInterest || !bisFeedCode
      ? '$shopify:::$default:::' + variantId
      : '$custom:::$default:::' + variantId + '-' + bisFeedCode;

    var drawer =
      global.document &&
      global.document.querySelector('#universal-drawer .Drawer__Content');
    if (!drawer) return;

    drawer.innerHTML =
      '<div class="drawer-contact-us drawer-scrollable">' +
      '<div class="drawer__header">Notify Me</div>' +
      '<div style="display:flex;flex-direction:column;gap:12px" data-swym-waitlist-form>' +
      '<input type="email" data-swym-waitlist-email' +
      ' style="width:100%;padding:12px 0;font-size:14px;border:none;border-bottom:1px solid var(--border-color,#e5e5e5);background:transparent;border-radius:0;outline:none"' +
      ' placeholder="Email address" autocomplete="email" required>' +
      '<label style="display:flex;align-items:center;gap:7px;cursor:pointer;margin:0">' +
      '<input type="checkbox" data-swym-waitlist-newsletter' +
      ' style="-webkit-appearance:none;appearance:none;width:10px;height:10px;margin:0;flex-shrink:0;border:1px solid #000;background:transparent;border-radius:0;cursor:pointer"' +
      ' data-swym-waitlist-newsletter>' +
      '<span style="font-size:13px;line-height:1.65">I would also like to sign up for the newsletter</span>' +
      '</label>' +
      '<div style="font-size:13px;color:#c00;display:none" data-swym-waitlist-error></div>' +
      '<button type="button"' +
      ' style="width:100%;height:30px;background:transparent;border:1px solid var(--border-color,#e5e5e5);font-size:13px;font-weight:400;letter-spacing:0;text-transform:none;cursor:pointer;padding:0 10px;display:flex;align-items:center;justify-content:center;color:var(--text-color);transition:background 0.2s,color 0.2s,border-color 0.2s"' +
      ' data-swym-waitlist-submit>' +
      '<span data-swym-waitlist-btn-text>Join The Waitlist</span>' +
      '<div class="btn-loader loader" style="display:none" data-swym-waitlist-loader></div>' +
      '</button>' +
      '</div>' +
      '<div style="display:none;font-size:13px;color:#6a6a6a;line-height:1.65" data-swym-waitlist-success>' +
      'Thank you. You will receive an email when this item is back in stock.' +
      '</div>' +
      '</div>';

    var form = drawer.querySelector('[data-swym-waitlist-form]');
    var emailInput = drawer.querySelector('[data-swym-waitlist-email]');
    var newsletterCheckbox = drawer.querySelector('[data-swym-waitlist-newsletter]');
    var submitBtn = drawer.querySelector('[data-swym-waitlist-submit]');
    var btnText =
      submitBtn && submitBtn.querySelector('[data-swym-waitlist-btn-text]');
    var btnLoader =
      submitBtn && submitBtn.querySelector('[data-swym-waitlist-loader]');
    var errorDisplay = drawer.querySelector('[data-swym-waitlist-error]');
    var successDisplay = drawer.querySelector('[data-swym-waitlist-success]');

    if (newsletterCheckbox) {
      newsletterCheckbox.addEventListener('change', function () {
        this.style.background = this.checked ? '#000' : 'transparent';
        this.style.boxShadow = this.checked ? 'inset 0 0 0 1px #fff' : 'none';
      });
    }

    submitBtn.addEventListener('mouseenter', function () {
      if (!this.disabled) {
        this.style.background = 'var(--text-color)';
        this.style.color = 'var(--background)';
        this.style.borderColor = 'var(--text-color)';
      }
    });
    submitBtn.addEventListener('mouseleave', function () {
      if (!this.disabled) {
        this.style.background = 'transparent';
        this.style.color = 'var(--text-color)';
        this.style.borderColor = 'var(--border-color, #e5e5e5)';
      }
    });

    submitBtn.addEventListener('click', function () {
      var email = emailInput ? emailInput.value.trim() : '';
      if (!email) {
        if (errorDisplay) {
          errorDisplay.textContent = 'Please enter your email address.';
          errorDisplay.style.display = '';
        }
        return;
      }

      if (errorDisplay) errorDisplay.style.display = 'none';
      submitBtn.disabled = true;
      if (btnText) btnText.style.display = 'none';
      if (btnLoader) btnLoader.style.display = '';

      fetch(
        'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' +
          global.theme.klaviyoPublicKey,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            revision: '2024-05-15',
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email: email },
                  },
                  email: email,
                },
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: variantRelationshipId,
                  },
                },
              },
            },
          }),
        },
      )
        .then(function (response) {
          if (response.ok) {
            if (form) form.style.display = 'none';
            if (successDisplay) successDisplay.style.display = '';

            if (newsletterCheckbox && newsletterCheckbox.checked) {
              fetch(
                'https://a.klaviyo.com/client/subscriptions?company_id=' +
                  global.theme.klaviyoPublicKey,
                {
                  method: 'POST',
                  headers: {
                    revision: '2024-10-15',
                    'content-type': 'application/vnd.api+json',
                  },
                  body: JSON.stringify({
                    data: {
                      type: 'subscription',
                      attributes: {
                        profile: {
                          data: {
                            type: 'profile',
                            attributes: { email: email },
                          },
                        },
                      },
                      relationships: {
                        list: {
                          data: {
                            type: 'list',
                            id: global.theme.klaviyoNewsLetterId,
                          },
                        },
                      },
                    },
                  }),
                },
              ).catch(function () {});
            }
          } else {
            throw new Error('Failed to subscribe');
          }
        })
        .catch(function () {
          if (errorDisplay) {
            errorDisplay.textContent =
              'There was an error subscribing. Please try again.';
            errorDisplay.style.display = '';
          }
          submitBtn.disabled = false;
          if (btnText) btnText.style.display = '';
          if (btnLoader) btnLoader.style.display = 'none';
        });
    });

    openUniversalDrawer();

    global.requestAnimationFrame(function () {
      if (emailInput) {
        emailInput.focus();
      }
    });
  }

  function invokeSwymDualCallback(fn, ctx, onSuccessPayload, onError) {
    var err = typeof onError === 'function' ? onError : function () {};
    try {
      fn.call(ctx, onSuccessPayload, err);
      return true;
    } catch (e1) {
      try {
        fn.call(ctx, {
          callbackFn: onSuccessPayload,
          errorFn: err,
        });
        return true;
      } catch (e2) {
        try {
          fn.call(ctx, function (data) {
            onSuccessPayload(data);
          });
          return true;
        } catch (e3) {
          return false;
        }
      }
    }
  }

  function extractArrayFromLegacyResponse(data) {
    if (data == null) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object') {
      if (Array.isArray(data.items)) return data.items;
      if (Array.isArray(data.products)) return data.products;
      if (Array.isArray(data.data)) return data.data;
    }
    return [];
  }

  function isSaveForLaterList(l) {
    var t = l && l.lty;
    if (t == null || t === '') return false;
    if (t === '_saveForLater') return true;
    var lower = String(t).toLowerCase();
    if (lower.replace(/_/g, '') === 'saveforlater') return true;
    return lower.indexOf('save') !== -1 && lower.indexOf('later') !== -1;
  }

  function normalizeFetchListsPayload(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.lists)) return raw.lists;
    return [];
  }

  function extractListItems(response) {
    if (!response) return [];
    if (Array.isArray(response.items)) return response.items;
    if (response.data && Array.isArray(response.data.items)) {
      return response.data.items;
    }
    return [];
  }

  function dedupeWishlistRowsKeepLast(rows) {
    var seen = Object.create(null);
    var out = [];
    var i;
    for (i = rows.length - 1; i >= 0; i--) {
      var it = rows[i];
      if (!it || it.epi == null) continue;
      var k = String(it.epi);
      if (seen[k]) continue;
      seen[k] = true;
      out.push(it);
    }
    out.reverse();
    return out;
  }

  function invokeFetchWishlist(swat, onSuccess, onError) {
    if (!swat || typeof swat.fetchWishlist !== 'function') return false;
    var err = typeof onError === 'function' ? onError : function () {};
    return invokeSwymDualCallback(
      swat.fetchWishlist,
      swat,
      function (data) {
        onSuccess(extractArrayFromLegacyResponse(data));
      },
      err,
    );
  }

  function wishlistEpiMatchesRow(item, data) {
    if (!item || item.epi == null || !data) return false;
    var ie = String(item.epi);
    if (data.epiStr != null && data.epiStr !== '' && ie === String(data.epiStr)) {
      return true;
    }
    if (data.epi != null && ie === String(data.epi)) return true;
    return false;
  }

  function lidForRemoveFromRows(items, data) {
    if (!items || !items.length) return null;
    var i;
    for (i = 0; i < items.length; i++) {
      if (wishlistEpiMatchesRow(items[i], data)) {
        return items[i].lid || null;
      }
    }
    return items[0].lid || null;
  }

  function epiForSwymApi(data) {
    if (typeof data.epi === 'number' && !isNaN(data.epi)) return data.epi;
    if (data.epiStr) {
      var n = parseInt(data.epiStr, 10);
      if (!isNaN(n)) return n;
    }
    return data.epi;
  }

  function productsJsonUrl(handle) {
    var root =
      typeof global.Shopify !== 'undefined' &&
      global.Shopify.routes &&
      global.Shopify.routes.root
        ? global.Shopify.routes.root
        : '/';
    if (root.slice(-1) !== '/') root += '/';
    return root + 'products/' + encodeURIComponent(handle) + '?view=wishlist-data';
  }

  function absoluteProductUrl(du) {
    if (!du) return '';
    if (/^https?:\/\//i.test(du)) return du;
    var origin = typeof global.location !== 'undefined' ? global.location.origin : '';
    if (du.charAt(0) === '/') return origin + du;
    return origin + '/' + du;
  }

  function pickWishlistList(lists) {
    if (!lists || lists.length === 0) return null;

    var nonSfl = lists.filter(function (l) {
      return !isSaveForLaterList(l);
    });
    if (nonSfl.length > 0) return nonSfl[0];

    var wl = lists.find(function (l) {
      return (l.lty || '').toLowerCase() === 'wl';
    });
    if (wl) return wl;

    return lists[0];
  }

  function rememberWishlistLid(lid) {
    if (lid == null || lid === '') return;
    try {
      global.sessionStorage.setItem(WISHLIST_LID_STORAGE_KEY, String(lid));
    } catch (e) {
      /* private mode / quota */
    }
  }

  function readStoredWishlistLid() {
    try {
      return global.sessionStorage.getItem(WISHLIST_LID_STORAGE_KEY) || null;
    } catch (e) {
      return null;
    }
  }

  function removeVariantFromSwym(swat, data, onSuccess, onError) {
    var errFn = typeof onError === 'function' ? onError : function () {};
    var okFn = typeof onSuccess === 'function' ? onSuccess : function () {};

    if (!swat || typeof swat.deleteFromList !== 'function') {
      errFn();
      return;
    }

    var product = {
      epi: epiForSwymApi(data),
      empi: data.empi,
      du: data.du,
    };

    function doDelete(lid) {
      if (!lid) {
        errFn();
        return;
      }
      swat.deleteFromList(
        lid,
        product,
        function () {
          okFn();
        },
        function () {
          errFn();
        },
      );
    }

    function fallbackLegacyLid() {
      if (
        !invokeFetchWishlist(
          swat,
          function (items) {
            doDelete(lidForRemoveFromRows(items, data));
          },
          errFn,
        )
      ) {
        errFn();
      }
    }

    if (typeof swat.fetchLists !== 'function') {
      fallbackLegacyLid();
      return;
    }

    swat.fetchLists({
      callbackFn: function (rawLists) {
        var lists = normalizeFetchListsPayload(rawLists);
        var withLid = lists.filter(function (l) {
          return l && l.lid;
        });
        if (!withLid.length) {
          fallbackLegacyLid();
          return;
        }

        function tryList(i) {
          if (i >= withLid.length) {
            fallbackLegacyLid();
            return;
          }
          var meta = withLid[i];
          swat.fetchListDetails(
            { lid: meta.lid },
            function (response) {
              var items = extractListItems(response);
              var has = items.some(function (it) {
                return wishlistEpiMatchesRow(it, data);
              });
              if (has) {
                doDelete(meta.lid);
              } else {
                tryList(i + 1);
              }
            },
            function () {
              tryList(i + 1);
            },
          );
        }

        tryList(0);
      },
      errorFn: function () {
        fallbackLegacyLid();
      },
    });
  }

  // Shopify's standard money_format placeholder parser (mirrors the
  // placeholder switch in theme.js's internal Currency.formatMoney, which is
  // module-private and unreachable from these standalone scripts — callers
  // here already guard cents with `typeof cents !== 'number'`, so this skips
  // Currency.formatMoney's own NaN/string-cents handling). Shared here so
  // swym-cart-saved.js and swym-wishlist.js don't each carry their own copy
  // (ROW-952).
  function formatMoneyFromFormat(cents, format) {
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholderRegex);
    if (!match) return null;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    // Keep trailing zeros — Saved-tab/wishlist prices use `| money` parity
    // with PDP/PLP/cart line items, unlike the cart's own discount and
    // free-shipping-threshold lines, which still strip them (ROW-952).
    return format.replace(placeholderRegex, value);
  }

  // Shared by plp.js and ProductCardInteractions.js's swatch-click price
  // update (previously two near-identical copies). EUR/USD go through the
  // theme's own presentment moneyFormat (same as PDP/PLP/cart); every other
  // market builds a leading-symbol / comma-thousands string directly rather
  // than via Intl — Intl trails the symbol for de-DE, appends a localized
  // ISO suffix for CAD/AUD/NZD/HKD (the theme already renders that suffix as
  // its own sibling span), and emits a fullwidth "￥" for ja, none of
  // which match Liquid's `| money` output (ROW-952).
  function formatMarketMoney(cents) {
    if (cents === null || cents === '' || typeof cents === 'boolean') return '';
    var n = Number(cents);
    if (!isFinite(n)) return '';

    var currency = (global.theme && global.theme.currencyCode) || 'USD';
    var formatString = (global.theme && global.theme.moneyFormat) || null;
    var formatMatchesMarket = currency === 'EUR' || currency === 'USD';

    if (formatString && formatMatchesMarket) {
      var fromFormat = formatMoneyFromFormat(n, formatString);
      if (fromFormat != null) return fromFormat;
    }

    var SYMBOLS = {
      USD: '$', CAD: '$', AUD: '$', NZD: '$', HKD: '$',
      GBP: '£', EUR: '€', JPY: '¥', KRW: '₩',
    };
    var NO_DECIMALS = { JPY: true, KRW: true };
    var symbol = SYMBOLS[currency] || currency + ' ';
    var amount = formatWithDelimitersShared(n, NO_DECIMALS[currency] ? 0 : 2);
    return symbol + amount;
  }

  function formatWithDelimitersShared(cents, precision) {
    var number = (cents / 100).toFixed(precision);
    var parts = number.split('.');
    var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    var centsAmount = parts[1] ? '.' + parts[1] : '';
    return dollarsAmount + centsAmount;
  }

  global.SwymWishlistCore = {
    WISHLIST_LID_STORAGE_KEY: WISHLIST_LID_STORAGE_KEY,
    formatMoneyFromFormat: formatMoneyFromFormat,
    formatMarketMoney: formatMarketMoney,
    getRegion: getRegion,
    getMarketContext: getMarketContext,
    isPreorderForRegion: isPreorderForRegion,
    resolveRegionalAvailability: resolveRegionalAvailability,
    getButtonState: getButtonState,
    openNotifyMeForVariant: openNotifyMeForVariant,
    openCartDrawer: openCartDrawer,
    invokeSwymDualCallback: invokeSwymDualCallback,
    extractArrayFromLegacyResponse: extractArrayFromLegacyResponse,
    isSaveForLaterList: isSaveForLaterList,
    normalizeFetchListsPayload: normalizeFetchListsPayload,
    extractListItems: extractListItems,
    dedupeWishlistRowsKeepLast: dedupeWishlistRowsKeepLast,
    invokeFetchWishlist: invokeFetchWishlist,
    wishlistEpiMatchesRow: wishlistEpiMatchesRow,
    lidForRemoveFromRows: lidForRemoveFromRows,
    epiForSwymApi: epiForSwymApi,
    productsJsonUrl: productsJsonUrl,
    absoluteProductUrl: absoluteProductUrl,
    preOrderProperties: preOrderProperties,
    preOrderPropertiesAttr: preOrderPropertiesAttr,
    readPreOrderProperties: readPreOrderProperties,
    sellingPlanAttr: sellingPlanAttr,
    readSellingPlan: readSellingPlan,
    pickWishlistList: pickWishlistList,
    rememberWishlistLid: rememberWishlistLid,
    readStoredWishlistLid: readStoredWishlistLid,
    removeVariantFromSwym: removeVariantFromSwym,
  };
})(typeof window !== 'undefined' ? window : this);
